import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SkeletonLoader } from "@/components/ui/skeleton-loader";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { useRecentTransactions } from "@/hooks/queries";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { parseLocalDate } from "@/lib/dates";

const Trends = () => {
  const { data: transactions = [], isLoading } = useRecentTransactions(6);
  const { formatCurrency } = useUserPreferences();

  const prepareChartData = () => {
    const monthlyData: { [key: string]: { month: string; income: number; expenses: number } } = {};

    transactions.forEach((t) => {
      const monthKey = format(parseLocalDate(t.date), "MMM yyyy");
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, income: 0, expenses: 0 };
      }
      if (t.type === "income") {
        monthlyData[monthKey].income += Number(t.amount);
      } else {
        monthlyData[monthKey].expenses += Number(t.amount);
      }
    });

    return Object.values(monthlyData);
  };

  const prepareCategoryData = () => {
    const categoryData: { [key: string]: { month: string; [category: string]: number | string } } = {};

    transactions
      .filter((t) => t.type === "expense")
      .forEach((t) => {
        const monthKey = format(parseLocalDate(t.date), "MMM yyyy");
        if (!categoryData[monthKey]) {
          categoryData[monthKey] = { month: monthKey };
        }
        categoryData[monthKey][t.category] = 
          (Number(categoryData[monthKey][t.category]) || 0) + Number(t.amount);
      });

    return Object.values(categoryData);
  };

  const categories = Array.from(new Set(transactions.filter(t => t.type === "expense").map(t => t.category)));
  const colors = ["#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444", "#8b5cf6"];

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded" />
          </div>
          <SkeletonLoader variant="chart" />
          <SkeletonLoader variant="chart" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-8"
      >
        <div>
          <h1 className="text-3xl font-bold mb-2">Spending Trends</h1>
          <p className="text-muted-foreground">Track your income and expenses over time</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Income vs Expenses (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={prepareChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="hsl(var(--income))" strokeWidth={2} />
                <Line type="monotone" dataKey="expenses" stroke="hsl(var(--expense))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Expenses by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={prepareCategoryData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                {categories.map((category, index) => (
                  <Line 
                    key={category} 
                    type="monotone" 
                    dataKey={category} 
                    stroke={colors[index % colors.length]} 
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Trends;